// @mui
import { Box, Link, ListItemText, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

//
import type { NavItemProps } from '../types';

// auth
import RoleBasedGuard from '../../../auth/RoleBasedGuard';
// locales
import { useLocales } from '../../../locales';
//
import Iconify from '../../iconify';
import { StyledDotIcon, StyledIcon, StyledItem } from './styles';

const NavItem = ({
  active,
  depth,
  isExternalLink,
  item,
  open,
  ...other
}: NavItemProps) => {
  const { translate } = useLocales();

  const { caption, children, disabled, icon, info, path, roles, title } = item;

  const subItem = depth !== 1;

  const renderContent = (
    <StyledItem
      active={active}
      caption={!!caption}
      depth={depth}
      disabled={disabled}
      {...other}
    >
      {icon && <StyledIcon>{icon}</StyledIcon>}

      {subItem && (
        <StyledIcon>
          <StyledDotIcon active={active && subItem} />
        </StyledIcon>
      )}

      <ListItemText
        primary={`${translate(title)}`}
        primaryTypographyProps={{
          component: 'span',
          noWrap: true,
          variant: active ? 'subtitle2' : 'body2',
        }}
        secondary={
          caption && (
            <Tooltip placement="top-start" title={`${translate(caption)}`}>
              <span>{`${translate(caption)}`}</span>
            </Tooltip>
          )
        }
        secondaryTypographyProps={{
          noWrap: true,
          variant: 'caption',
        }}
      />

      {info && (
        <Box component="span" sx={{ lineHeight: 0 }}>
          {info}
        </Box>
      )}

      {!!children && (
        <Iconify
          icon={
            open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'
          }
          sx={{ flexShrink: 0, ml: 1 }}
          width={16}
        />
      )}
    </StyledItem>
  );

  const renderItem = () => {
    // ExternalLink
    if (isExternalLink)
      return (
        <Link href={path} rel="noopener" target="_blank" underline="none">
          {renderContent}
        </Link>
      );

    // Has child
    if (children) {
      return renderContent;
    }

    // Default
    return (
      <Link component={RouterLink} to={path} underline="none">
        {renderContent}
      </Link>
    );
  };

  return <RoleBasedGuard roles={roles}> {renderItem()} </RoleBasedGuard>;
};

export default NavItem;
